import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Chip,
  IconButton, Collapse, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Alert, Tooltip, InputAdornment, Snackbar,
  ToggleButton, ToggleButtonGroup, Skeleton, Divider
} from '@mui/material';
import {
  Restaurant, Add, ExpandMore, ExpandLess, Schedule, CheckCircle,
  WbSunny, LightMode, WbTwilight, NightsStay, TrendingUp,
  AccessTime, LocalDining, Scale, Notes, Category
} from '@mui/icons-material';
import { feedEventsAPI } from 'services/api';

// ============================================================================
// CONFIGURATION
// ============================================================================

const TIME_SLOTS = [
  { value: 'morning', label: 'Morning', icon: <WbSunny />, time: '06:00 - 10:00', color: '#FF9800' },
  { value: 'afternoon', label: 'Afternoon', icon: <LightMode />, time: '11:00 - 15:00', color: '#FFC107' },
  { value: 'evening', label: 'Evening', icon: <WbTwilight />, time: '16:00 - 19:00', color: '#FF5722' },
  { value: 'night', label: 'Night', icon: <NightsStay />, time: '20:00 - 22:00', color: '#5C6BC0' }
];

const FEED_TYPES = [
  { value: 'starter', label: 'Starter Feed' },
  { value: 'grower', label: 'Grower Feed' },
  { value: 'finisher', label: 'Finisher Feed' },
  { value: 'pellet', label: 'Pellet Feed' },
  { value: 'crumble', label: 'Crumble Feed' },
  { value: 'supplement', label: 'Supplement' },
  { value: 'other', label: 'Other' }
];

const FEED_SIZES = ['0.5mm', '0.8mm', '1.0mm', '1.2mm', '1.5mm', '1.8mm', '2.0mm', '2.5mm', '3.0mm'];

// ============================================================================
// NEXT FEED CALCULATION (Rule-based)
// ============================================================================

const calculateNextFeed = (lastFeedTime, lastSlot) => {
  if (!lastFeedTime) return null;
  
  const lastFeed = new Date(lastFeedTime);
  const now = new Date();
  
  // Define feeding schedule (4 feeds per day)
  const feedingSchedule = {
    morning: { next: 'afternoon', hours: 4 },
    afternoon: { next: 'evening', hours: 4 },
    evening: { next: 'night', hours: 4 },
    night: { next: 'morning', hours: 8 } // Longer gap overnight
  };
  
  const schedule = feedingSchedule[lastSlot] || feedingSchedule.morning;
  const nextFeedTime = new Date(lastFeed.getTime() + (schedule.hours * 60 * 60 * 1000));
  
  // If next feed time has passed, return "now" or the next slot
  if (nextFeedTime < now) {
    const currentHour = now.getHours();
    if (currentHour >= 6 && currentHour < 11) return { slot: 'morning', time: 'Now', overdue: true };
    if (currentHour >= 11 && currentHour < 16) return { slot: 'afternoon', time: 'Now', overdue: true };
    if (currentHour >= 16 && currentHour < 20) return { slot: 'evening', time: 'Now', overdue: true };
    return { slot: 'night', time: 'Now', overdue: true };
  }
  
  return { 
    slot: schedule.next, 
    time: nextFeedTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }),
    overdue: false
  };
};

// ============================================================================
// FEED STATUS DISPLAY COMPONENT
// ============================================================================

const FeedStatusDisplay = ({ lastFeed, pondId }) => {
  const lastSlot = lastFeed?.feeding_time_slot?.toLowerCase();
  const nextFeed = calculateNextFeed(lastFeed?.feed_date_time, lastSlot);
  const slotConfig = TIME_SLOTS.find(s => s.value === lastSlot) || TIME_SLOTS[0];
  const nextSlotConfig = nextFeed ? TIME_SLOTS.find(s => s.value === nextFeed.slot) : null;

  return (
    <Box sx={{ 
      display: 'flex', 
      gap: 2, 
      mb: 2,
      flexWrap: 'wrap'
    }}>
      {/* Last Feed */}
      <Box sx={{ 
        flex: 1,
        minWidth: 160,
        p: 1.5, 
        bgcolor: 'success.50', 
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'success.200'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} />
          <Typography variant="caption" sx={{ 
            color: 'success.main', 
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontSize: '0.65rem'
          }}>
            Last Feed
          </Typography>
        </Box>
        {lastFeed ? (
          <>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1.1rem' }}>
              {lastFeed.quantity_kg} kg
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <Box sx={{ color: slotConfig.color }}>
                {React.cloneElement(slotConfig.icon, { sx: { fontSize: 14 } })}
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                {new Date(lastFeed.feed_date_time).toLocaleTimeString('en-IN', { 
                  hour: '2-digit', 
                  minute: '2-digit',
                  hour12: true 
                })}
                {' • '}
                {slotConfig.label}
              </Typography>
            </Box>
          </>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No feed recorded
          </Typography>
        )}
      </Box>

      {/* Next Feed */}
      <Box sx={{ 
        flex: 1,
        minWidth: 160,
        p: 1.5, 
        bgcolor: nextFeed?.overdue ? 'warning.50' : 'info.50', 
        borderRadius: 2,
        border: '1px solid',
        borderColor: nextFeed?.overdue ? 'warning.200' : 'info.200'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Schedule sx={{ fontSize: 14, color: nextFeed?.overdue ? 'warning.main' : 'info.main' }} />
          <Typography variant="caption" sx={{ 
            color: nextFeed?.overdue ? 'warning.main' : 'info.main', 
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontSize: '0.65rem'
          }}>
            Next Feed
          </Typography>
          {nextFeed?.overdue && (
            <Chip 
              label="Due" 
              size="small" 
              color="warning"
              sx={{ height: 16, fontSize: '0.6rem', ml: 0.5 }}
            />
          )}
        </Box>
        {nextFeed && nextSlotConfig ? (
          <>
            <Typography variant="h6" sx={{ 
              fontWeight: 700, 
              color: nextFeed.overdue ? 'warning.main' : 'text.primary',
              fontSize: '1.1rem'
            }}>
              {nextFeed.time}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <Box sx={{ color: nextSlotConfig.color }}>
                {React.cloneElement(nextSlotConfig.icon, { sx: { fontSize: 14 } })}
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                {nextSlotConfig.label} • {nextSlotConfig.time}
              </Typography>
            </Box>
          </>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Start feeding to track
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// ============================================================================
// MAIN INLINE FEED ENTRY COMPONENT
// ============================================================================

const InlineFeedEntry = ({ pondId, pondName, onFeedAdded, compact = false }) => {
  // Form state
  const [quantity, setQuantity] = useState('');
  const [timeSlot, setTimeSlot] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [feedType, setFeedType] = useState('');
  const [feedSize, setFeedSize] = useState('');
  const [remarks, setRemarks] = useState('');
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [lastFeed, setLastFeed] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Detect current time slot
  const detectCurrentSlot = () => {
    const hour = new Date().getHours();
    if (hour >= 6 && hour < 11) return 'morning';
    if (hour >= 11 && hour < 16) return 'afternoon';
    if (hour >= 16 && hour < 20) return 'evening';
    return 'night';
  };

  // Load last feed status
  const loadFeedStatus = useCallback(async () => {
    try {
      setLoadingStatus(true);
      const response = await feedEventsAPI.getTimeline(pondId, 1);
      const feeds = response.data || [];
      setLastFeed(feeds[0] || null);
    } catch (err) {
      console.error('Error loading feed status:', err);
    } finally {
      setLoadingStatus(false);
    }
  }, [pondId]);

  useEffect(() => {
    if (pondId) {
      loadFeedStatus();
      // Set default time slot to current
      setTimeSlot(detectCurrentSlot());
    }
  }, [pondId, loadFeedStatus]);

  // Handle form submission
  const handleSubmit = async () => {
    if (!quantity || parseFloat(quantity) <= 0) {
      setError('Please enter a valid quantity');
      return;
    }
    if (!timeSlot) {
      setError('Please select a feeding time slot');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const feedData = {
        quantity_kg: parseFloat(quantity),
        feeding_time_slot: timeSlot,
        feed_date_time: new Date().toISOString(),
        ...(feedType && { feed_type: feedType }),
        ...(feedSize && { feed_size_mm: parseFloat(feedSize.replace('mm', '')) }),
        ...(remarks && { remarks: remarks.trim() })
      };

      await feedEventsAPI.create(pondId, feedData);
      
      // Success
      setSuccess(true);
      setQuantity('');
      setShowAdvanced(false);
      setFeedType('');
      setFeedSize('');
      setRemarks('');
      
      // Reload status
      await loadFeedStatus();
      
      // Callback to parent
      if (onFeedAdded) {
        onFeedAdded();
      }

    } catch (err) {
      console.error('Error adding feed:', err);
      setError(err.response?.data?.detail || 'Failed to add feed entry');
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setQuantity('');
    setTimeSlot(detectCurrentSlot());
    setShowAdvanced(false);
    setFeedType('');
    setFeedSize('');
    setRemarks('');
    setError(null);
  };

  return (
    <Card sx={{ 
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'success.light',
      background: 'linear-gradient(135deg, #f1f8e9 0%, #e8f5e9 100%)'
    }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Restaurant sx={{ color: 'success.main', fontSize: 22 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'success.main' }}>
            Feed Entry
          </Typography>
          {pondName && (
            <Chip 
              label={pondName}
              size="small"
              sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'white' }}
            />
          )}
        </Box>

        {/* Feed Status Display */}
        {loadingStatus ? (
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Skeleton variant="rounded" height={80} sx={{ flex: 1 }} />
            <Skeleton variant="rounded" height={80} sx={{ flex: 1 }} />
          </Box>
        ) : (
          <FeedStatusDisplay lastFeed={lastFeed} pondId={pondId} />
        )}

        {/* Error Alert */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, py: 0.5 }}>
            {error}
          </Alert>
        )}

        {/* Quick Entry Form */}
        <Box sx={{ 
          p: 1.5, 
          bgcolor: 'white', 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200'
        }}>
          {/* Main Input Row */}
          <Box sx={{ 
            display: 'flex', 
            gap: 1.5, 
            alignItems: 'flex-start',
            flexWrap: 'wrap'
          }}>
            {/* Quantity Input */}
            <TextField
              size="small"
              label="Quantity"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="0.0"
              InputProps={{
                endAdornment: <InputAdornment position="end">kg</InputAdornment>,
                startAdornment: (
                  <InputAdornment position="start">
                    <Scale sx={{ fontSize: 16, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                sx: { fontSize: '0.9rem' }
              }}
              sx={{ 
                width: 130,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'grey.50'
                }
              }}
              inputProps={{ min: 0, step: 0.1 }}
            />

            {/* Time Slot Selection */}
            <ToggleButtonGroup
              value={timeSlot}
              exclusive
              onChange={(e, value) => value && setTimeSlot(value)}
              size="small"
              sx={{ 
                bgcolor: 'grey.50',
                '& .MuiToggleButton-root': {
                  px: 1.5,
                  py: 0.75,
                  borderColor: 'grey.300',
                  '&.Mui-selected': {
                    bgcolor: 'success.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'success.dark'
                    }
                  }
                }
              }}
            >
              {TIME_SLOTS.map((slot) => (
                <ToggleButton 
                  key={slot.value} 
                  value={slot.value}
                  sx={{ minWidth: 'auto' }}
                >
                  <Tooltip title={`${slot.label} (${slot.time})`}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {React.cloneElement(slot.icon, { 
                        sx: { fontSize: 16, color: timeSlot === slot.value ? 'inherit' : slot.color } 
                      })}
                      {!compact && (
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                          {slot.label.substring(0, 3)}
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            {/* Add Feed Button */}
            <Button
              variant="contained"
              color="success"
              onClick={handleSubmit}
              disabled={loading || !quantity || !timeSlot}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Add />}
              sx={{ 
                minWidth: 100,
                fontWeight: 600,
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)'
                }
              }}
            >
              {loading ? 'Adding...' : 'Add Feed'}
            </Button>
          </Box>

          {/* Advanced Toggle */}
          <Box sx={{ mt: 1.5 }}>
            <Button
              size="small"
              onClick={() => setShowAdvanced(!showAdvanced)}
              endIcon={showAdvanced ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
              sx={{ 
                fontSize: '0.75rem', 
                color: 'text.secondary',
                textTransform: 'none',
                p: 0.5
              }}
            >
              {showAdvanced ? 'Hide details' : 'Add details (optional)'}
            </Button>
          </Box>

          {/* Advanced Fields */}
          <Collapse in={showAdvanced}>
            <Box sx={{ 
              mt: 1.5, 
              pt: 1.5, 
              borderTop: '1px dashed',
              borderColor: 'grey.200',
              display: 'flex',
              gap: 1.5,
              flexWrap: 'wrap'
            }}>
              {/* Feed Type */}
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel sx={{ fontSize: '0.8rem' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Category sx={{ fontSize: 14 }} />
                    Feed Type
                  </Box>
                </InputLabel>
                <Select
                  value={feedType}
                  label="Feed Type"
                  onChange={(e) => setFeedType(e.target.value)}
                  sx={{ fontSize: '0.8rem', bgcolor: 'grey.50' }}
                >
                  <MenuItem value="" sx={{ fontSize: '0.8rem' }}>
                    <em>None</em>
                  </MenuItem>
                  {FEED_TYPES.map(type => (
                    <MenuItem key={type.value} value={type.value} sx={{ fontSize: '0.8rem' }}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Feed Size */}
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel sx={{ fontSize: '0.8rem' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <LocalDining sx={{ fontSize: 14 }} />
                    Size
                  </Box>
                </InputLabel>
                <Select
                  value={feedSize}
                  label="Size"
                  onChange={(e) => setFeedSize(e.target.value)}
                  sx={{ fontSize: '0.8rem', bgcolor: 'grey.50' }}
                >
                  <MenuItem value="" sx={{ fontSize: '0.8rem' }}>
                    <em>None</em>
                  </MenuItem>
                  {FEED_SIZES.map(size => (
                    <MenuItem key={size} value={size} sx={{ fontSize: '0.8rem' }}>
                      {size}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Remarks */}
              <TextField
                size="small"
                label="Remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Optional notes..."
                multiline
                maxRows={2}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Notes sx={{ fontSize: 14, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  sx: { fontSize: '0.8rem' }
                }}
                sx={{ 
                  flex: 1,
                  minWidth: 150,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'grey.50'
                  }
                }}
              />
            </Box>
          </Collapse>
        </Box>

        {/* Today's Summary */}
        {lastFeed && (
          <Box sx={{ 
            mt: 2, 
            p: 1, 
            bgcolor: 'white', 
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TrendingUp sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                Feed events are automatically recorded in the timeline
              </Typography>
            </Box>
            <Chip
              icon={<AccessTime sx={{ fontSize: 12 }} />}
              label={`Last: ${new Date(lastFeed.feed_date_time).toLocaleTimeString('en-IN', { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: true 
              })}`}
              size="small"
              variant="outlined"
              sx={{ height: 22, fontSize: '0.65rem' }}
            />
          </Box>
        )}
      </CardContent>

      {/* Success Snackbar */}
      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        message={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle sx={{ fontSize: 18 }} />
            Feed entry added successfully!
          </Box>
        }
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Card>
  );
};

export default InlineFeedEntry;
